import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import type {TFunction} from 'i18next';
import type {ReactNode} from 'react';
import {
  STATUSES,
  getFeatureFlags,
  isPassportDocType,
  type DocumentChoice,
} from '@chekin/core';
import {
  HighlightedTitle,
  type HighlightedTitleTone,
} from '../../../components/PageLayoutHeader/HighlightedTitle';
import type {IVFailureReason} from '../../../hooks/iv/documentSelfieDetection/types';
import {getIVErrorTitleKey} from './ivErrorTitle';
import {useCaptureModeStore} from '../../../components/capture/useCaptureModeStore';
import {useCaptureErrorRevealed} from '../../../components/capture/useCaptureErrorRevealed';
import type {CaptureCheck} from '../../../components/capture/CaptureCheckingView';
import {useLivenessStore} from '../../../components/LivenessDetection/store/useLivenessStore';
import type {LivenessStep} from '../../../components/LivenessDetection/types';

export type IVCaptureStep = 'front' | 'back' | 'document' | 'selfie' | 'liveness';

export type IVCaptureDocumentType = Pick<
  DocumentChoice,
  'two_sides' | 'without_mrz' | 'both_sides_mrz'
> &
  Partial<Pick<DocumentChoice, 'value'>>;

export type IVCapturePresentation = 'frame' | 'ring';

type IVCapturePart = 'front' | 'back' | 'photo_page' | 'face';

function getCapturePart(
  step: IVCaptureStep,
  documentType?: IVCaptureDocumentType | null,
): IVCapturePart {
  if (step === 'back') return 'back';
  if (step === 'selfie' || step === 'liveness') return 'face';
  if (
    step === 'document' &&
    documentType?.value &&
    isPassportDocType(documentType.value)
  ) {
    return 'photo_page';
  }
  return 'front';
}

const INTRO_TITLE_KEY: Record<IVCapturePart, string> = {
  front: 'photograph_front_side',
  back: 'photograph_back_side_now',
  photo_page: 'photograph_photo_page',
  face: 'get_ready_for_selfie',
};

const CAMERA_TITLE_KEY: Record<IVCapturePart, string> = {
  front: 'fit_front_in_frame',
  back: 'fit_back_in_frame',
  photo_page: 'fit_photo_page_in_frame',
  face: 'fit_face_in_oval',
};

export function getIVCaptureChecks(
  step: IVCaptureStep,
  {biomatch, withoutMrz}: {biomatch?: boolean; withoutMrz?: boolean} = {},
): CaptureCheck[] {
  switch (step) {
    case 'front':
      return ['face', 'sharp'];
    case 'back':
      return ['lines', 'sharp'];
    case 'document':
      if (!biomatch) return ['lines', 'sharp'];
      return withoutMrz ? ['face', 'sharp'] : ['face', 'lines', 'sharp'];
    case 'selfie':
    case 'liveness':
      return ['face', 'compare'];
  }
}

export function showsCodeSide(
  step: IVCaptureStep,
  documentType?: IVCaptureDocumentType | null,
): boolean {
  if (step !== 'document' && step !== 'back') return false;
  return !documentType?.without_mrz;
}

export type IVCaptureCopy = {
  kicker: string;
  title: ReactNode;
  intro: ReactNode;
  statusLabel: string;
  scanningLabel: string;
  successTitle: string;
  successIntro: string;
  successLabel: string;
  errorTitle: string;
  errorDescription: string;
  confirmLabel: string;
};

const highlighted = (i18nKey: string, tone: HighlightedTitleTone = 'brand') => (
  <HighlightedTitle i18nKey={i18nKey} tone={tone} />
);

const highlightedIV = (i18nKey: string) => (
  <HighlightedTitle ns="identity-verification" i18nKey={i18nKey} />
);

const responsiveText = (t: TFunction, shortKey: string, longKey: string) => (
  <>
    <span className="lg:hidden">{t(shortKey)}</span>
    <span className="hidden lg:inline">{t(longKey)}</span>
  </>
);

export function getIVCaptureCopy(
  t: TFunction,
  step: IVCaptureStep,
  documentType?: IVCaptureDocumentType | null,
): IVCaptureCopy {
  const codeSide = showsCodeSide(step, documentType)
    ? {intro: null, statusLabel: t('show_code_side')}
    : {};
  const guestRegistration = t('guest_registration');
  const movingIntro = t('moving_to_next_step');

  const documentBase = {
    kicker: guestRegistration,
    intro: getFeatureFlags('IV_DOCUMENT_AUTO_CAPTURE')
      ? responsiveText(
          t,
          'photo_taken_automatically_hint_short',
          'photo_taken_automatically_hint',
        )
      : t('document_manual_hint'),
    statusLabel: t('place_document_inside_frame'),
    scanningLabel: t('scanning'),
    successTitle: t('photo_saved_exclamation'),
    successIntro: movingIntro,
    successLabel: t('photo_taken_exclamation'),
    errorTitle: t('document_validation_failed'),
    errorDescription: t('document_validation_failed_text'),
    confirmLabel: t('confirm'),
  };

  const selfieBase = {
    kicker: guestRegistration,
    scanningLabel: t('verification_in_progress'),
    successTitle: t('selfie_ready_exclamation'),
    successIntro: movingIntro,
    successLabel: t('selfie_captured_exclamation'),
    errorTitle: t('selfie_validation_failed'),
    errorDescription: t('selfie_validation_failed_text'),
    confirmLabel: t('confirm'),
  };

  switch (step) {
    case 'front':
      return {
        ...documentBase,
        title: highlighted('photo_of_the_front_title'),
        confirmLabel: t('confirm_front_side'),
      };
    case 'back':
      return {
        ...documentBase,
        title: highlighted('photo_of_the_back_title'),
        ...codeSide,
        confirmLabel: t('confirm_back_side'),
      };
    case 'document':
      return {
        ...documentBase,
        title: highlighted(
          showsCodeSide(step, documentType)
            ? 'photo_of_code_side_title'
            : 'photo_of_document_title',
        ),
        ...codeSide,
      };
    case 'selfie':
      return {
        ...selfieBase,
        title: highlighted('confirm_its_you_title'),
        intro: t('look_at_camera_auto_hint'),
        statusLabel: t('look_at_the_camera'),
      };
    case 'liveness':
      return {
        ...selfieBase,
        title: t('check_face'),
        intro: t('keep_your_face_inside_oval_frame'),
        statusLabel: t('look_at_the_camera'),
      };
  }
}

type UseIVCaptureScreenText = {
  copy: IVCaptureCopy;
  isIvSuccess: boolean;
  isChecking: boolean;
  title: ReactNode;
  intro: ReactNode;
};

const CROP_TITLE_KEY: Partial<Record<IVCaptureStep, string>> = {
  front: 'check_the_front_side_title',
  back: 'check_the_back_side_title',
  selfie: 'check_your_selfie_title',
};

const MANUAL_HINT_KEY: Partial<Record<IVCaptureStep, string>> = {
  front: 'document_manual_hint',
  back: 'document_manual_hint',
  document: 'document_manual_hint',
  selfie: 'look_at_camera_manual_hint',
};

export function useIVCaptureScreenText(
  step: IVCaptureStep,
  processState?: STATUSES,
  isProcessing?: boolean,
  options?: {
    autoCaptureUnavailable?: boolean;
    documentType?: IVCaptureDocumentType | null;
    presentation?: IVCapturePresentation;
    isIntro?: boolean;
    isFinalStep?: boolean;
    biomatch?: boolean;
    errorReason?: IVFailureReason;
    failureCount?: number;
  },
): UseIVCaptureScreenText {
  const {t} = useTranslation();
  const {t: tIV} = useTranslation('identity-verification');
  const isRing = options?.presentation === 'ring';
  const isIntro = Boolean(options?.isIntro);
  const isFinalStep = Boolean(options?.isFinalStep);
  const biomatch = Boolean(options?.biomatch);
  const autoCaptureUnavailable = options?.autoCaptureUnavailable;
  const documentType = options?.documentType;
  const errorTitle = tIV(
    getIVErrorTitleKey(
      step === 'selfie' || step === 'liveness',
      options?.errorReason,
      (options?.failureCount ?? 0) > 1,
    ),
  );
  const captureMode = useCaptureModeStore(state => state.mode);
  const isCropping = captureMode === 'crop';
  const isCameraDenied = captureMode === 'denied';
  const isErrorRevealed = useCaptureErrorRevealed(
    isRing && Boolean(isProcessing) && processState === STATUSES.error,
  );

  return useMemo(() => {
    const copy = getIVCaptureCopy(t, step, documentType);
    const isIvSuccess = Boolean(isProcessing) && processState === STATUSES.success;
    const isIvError =
      Boolean(isProcessing) &&
      processState === STATUSES.error &&
      (!isRing || isErrorRevealed);
    const isChecking = Boolean(isProcessing) && !isIvError;
    const showDenied = isCameraDenied && !isProcessing && !isCropping;

    const manualHintKey = MANUAL_HINT_KEY[step];
    const idleIntro =
      autoCaptureUnavailable && manualHintKey && !showsCodeSide(step, documentType)
        ? t(manualHintKey)
        : copy.intro;

    const getRingScreen = (): Pick<UseIVCaptureScreenText, 'title' | 'intro'> | null => {
      if (!isRing) return null;
      const part = getCapturePart(step, documentType);
      if (isChecking && !isIvSuccess) {
        return {title: tIV('checking_your_photo'), intro: tIV('keep_screen_open')};
      }
      if (isIvSuccess && isFinalStep) {
        return biomatch
          ? {
              title: highlightedIV('identity_verified'),
              intro: tIV('identity_verified_sub'),
            }
          : {
              title: highlightedIV('document_verified'),
              intro: tIV('document_verified_sub'),
            };
      }
      if (isIvSuccess) return {title: tIV('photo_verified'), intro: null};
      if (isCropping) return {title: highlightedIV(CAMERA_TITLE_KEY[part]), intro: null};
      if (isIvError) return {title: errorTitle, intro: null};
      if (showDenied) return null;
      if (isIntro) {
        return {
          title: highlightedIV(INTRO_TITLE_KEY[part]),
          intro: tIV(part === 'face' ? 'selfie_prep_intro' : 'follow_tips_sharp_photo'),
        };
      }
      if (step === 'liveness') return null;
      return {title: highlightedIV(CAMERA_TITLE_KEY[part]), intro: null};
    };

    const getScreen = (): Pick<UseIVCaptureScreenText, 'title' | 'intro'> => {
      const ringScreen = getRingScreen();
      if (ringScreen) return ringScreen;
      if (isIvSuccess) {
        return {title: copy.successTitle, intro: copy.successIntro};
      }
      if (isCropping) {
        return {
          title: highlighted(CROP_TITLE_KEY[step] ?? 'check_your_document_title'),
          intro: t('photo_loaded_drag_corners'),
        };
      }
      if (isIvError) {
        return {
          title: errorTitle,
          intro: copy.errorDescription,
        };
      }
      if (showDenied) {
        return {
          title: highlighted('camera_access_needed_title'),
          intro: t('camera_blocked_allow_access'),
        };
      }
      return {title: copy.title, intro: idleIntro};
    };

    return {copy, isIvSuccess, isChecking, ...getScreen()};
  }, [
    t,
    tIV,
    step,
    processState,
    isProcessing,
    autoCaptureUnavailable,
    documentType,
    isCropping,
    isCameraDenied,
    isRing,
    isErrorRevealed,
    errorTitle,
    isIntro,
    isFinalStep,
    biomatch,
  ]);
}

type LivenessStageCopy = {
  titleKey: string;
  introKey: string;
  tone?: HighlightedTitleTone;
};

const LIVENESS_STAGE_COPY: Record<
  'position' | 'challenge' | 'blink' | 'passed',
  LivenessStageCopy
> = {
  position: {
    titleKey: 'center_your_face_title',
    introKey: 'center_your_face_intro',
  },
  challenge: {
    titleKey: 'follow_the_prompts_title',
    introKey: 'follow_the_prompts_intro',
  },
  blink: {
    titleKey: 'almost_there_title',
    introKey: 'blink_slowly_twice_intro',
  },
  passed: {
    titleKey: 'liveness_passed_title',
    introKey: 'matching_face_with_document_photo',
    tone: 'success',
  },
};

const getLivenessStage = (step: LivenessStep) => {
  switch (step) {
    case 'LOOK_STRAIGHT':
      return 'position';
    case 'BLINK':
      return 'blink';
    case 'COMPLETED':
      return 'passed';
    default:
      return 'challenge';
  }
};

export function useLivenessStageText(): Pick<UseIVCaptureScreenText, 'title' | 'intro'> {
  const {t} = useTranslation();
  const currentStep = useLivenessStore(state => state.currentStep);
  const {titleKey, introKey, tone} = LIVENESS_STAGE_COPY[getLivenessStage(currentStep)];

  return {title: highlighted(titleKey, tone), intro: t(introKey)};
}
